import {
    FieldArray,
    FieldArrayRenderProps,
    useFormikContext,
} from 'formik';
import {
    ActionIcon,
    Alert,
    Autocomplete,
    Button,
    Group,
    NumberInput,
    Stack,
    Table,
    Text,
} from '@mantine/core';
import {
    AiOutlineDelete,
    AiOutlinePlus,
} from 'react-icons/ai';

import {useTestingActionsQuery} from '../../testing-actions/hooks/useTestingActionsQuery.ts';
import {ActionStatus} from '../../testing-actions/types.ts';
import {PersonaFormValues} from '../types.ts';

const DEFAULT_WEIGHT = 1;

// The action codes a persona performs, each with a weight (the executor runs
// them in descending weight order today; weighted sampling comes later).
//
// Suggestions come from ACTIVE actions only, but the input is a free-text
// Autocomplete rather than a Select: an actionCode that is not in the catalog is
// LEGAL. The executor fails OPEN — an unregistered code records an
// `unimplemented` observation and the wake continues. Constraining this to the
// catalog would break that seam, so the list is a convenience, never a gate.
export const WeightedActionCodesField = () => {

    const {
        values,
        errors,
    } = useFormikContext<PersonaFormValues>();

    const {data: actions = []} = useTestingActionsQuery();

    const activeActionCodes = actions
        .filter(action => action.status === ActionStatus.ACTIVE)
        .map(action => action.actionCode);

    const rows = values.actionCodes ?? [];
    const arrayError = typeof errors.actionCodes === 'string' ? errors.actionCodes : undefined;

    return (
        <FieldArray name='actionCodes'>
            {({
                  push,
                  remove,
                  form,
              }: FieldArrayRenderProps) => (
                <Stack gap={'sm'}>
                    <div>
                        <Text fw={500} size={'sm'}>Action Codes</Text>
                        <Text size={'xs'} c={'dimmed'}>
                            <strong>Deprecated.</strong> The executor no longer reads action
                            codes — a wake runs the persona&apos;s Test Definitions above.
                            Retained while existing personas are migrated.
                        </Text>
                        <Text size={'xs'} c={'dimmed'}>
                            Suggestions list ACTIVE actions, but any code is accepted — an
                            unregistered code is recorded as an &quot;unimplemented&quot;
                            observation rather than failing the wake. Higher weights run first.
                        </Text>
                    </div>

                    {rows.length === 0 ? (
                        <Alert color='gray' variant='light'>
                            No action codes yet. A persona with no action codes wakes and does nothing.
                        </Alert>
                    ) : (
                        <Table withTableBorder verticalSpacing={'xs'}>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Action Code</Table.Th>
                                    <Table.Th w={140}>Weight</Table.Th>
                                    <Table.Th w={60}/>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {rows.map((row, index) => (
                                    <Table.Tr key={index}>
                                        <Table.Td>
                                            <Autocomplete
                                                aria-label={`Action code ${index + 1}`}
                                                placeholder='user.login'
                                                data={activeActionCodes}
                                                value={row.actionCode}
                                                onChange={(value) =>
                                                    form.setFieldValue(`actionCodes.${index}.actionCode`, value)
                                                }
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <NumberInput
                                                aria-label={`Weight for action code ${index + 1}`}
                                                min={1}
                                                allowDecimal={false}
                                                value={row.weight}
                                                onChange={(value) =>
                                                    form.setFieldValue(
                                                        `actionCodes.${index}.weight`,
                                                        value === '' ? '' : Number(value)
                                                    )
                                                }
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <ActionIcon
                                                aria-label={`Remove action code ${index + 1}`}
                                                variant='subtle'
                                                color='red'
                                                onClick={() => remove(index)}
                                            >
                                                <AiOutlineDelete/>
                                            </ActionIcon>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    )}

                    {arrayError ? (
                        <Text size={'xs'} c={'red'}>{arrayError}</Text>
                    ) : null}

                    <Group>
                        <Button
                            variant='light'
                            leftSection={<AiOutlinePlus/>}
                            onClick={() => push({
                                                    actionCode: '',
                                                    weight: DEFAULT_WEIGHT,
                                                })}
                        >
                            Add Action Code
                        </Button>
                    </Group>
                </Stack>
            )}
        </FieldArray>
    );
};
